import { logger } from "@/utils/logger";

/**
 * Cookie同意の確定値。
 * - `"granted"`: 明示的に同意（analytics_storageが有効）
 * - `"denied"`: 明示的に拒否（cookieless pingのみ）
 *
 * 未選択(null)はここに含めない。未選択の間はupdateConsentを呼ばず、
 * GA4 Consent Modeのデフォルト値("denied")に委ねる。
 */
type ConsentState = "granted" | "denied";

let initialized = false;

// gtag.jsスクリプト読み込み完了を示すdeferred。
// モジュールレベルで保持することで、GA4Trackerがremountされても同一Promiseを再利用する。
// next/scriptのonLoadは初回読み込み時にしか発火しないため、
// remount時に新しいPromiseを作ると永久にresolveしない問題を防ぐ。
//
// ga4ReadyState自体はexportせず、resolveはcreateScriptHandlersが返す
// onLoad/onError 経由でのみ行う（外部から状態を自由に操作できないよう、モジュール内にカプセル化する）。
const ga4ReadyState = Promise.withResolvers<void>();

/**
 * dataLayer/gtagスタブとConsent Modeデフォルトを初期化する。
 *
 * GA4 Consent Mode の仕様上、`gtag("consent", "default", ...)` は gtag.js の実行
 * *前* に dataLayer に積まれている必要がある。そうでないとデフォルトがdeniedとして
 * 登録されず、cookieless pingのフォールバックが機能しない。
 *
 * `initialized` フラグで冪等化しているため、StrictMode dev の二重呼び出しでも安全。
 */
function setupGA4() {
  if (initialized) {
    return;
  }
  initialized = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // GTM/GA4公式スニペット互換のため `arguments` を使用する。
    // rest paramsに書き換えるとdataLayerにpushされるオブジェクトの形状が
    // IArgumentsから配列に変わり、GA4処理系との互換性が崩れるリスクがある。
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());

  // Consent Modeのデフォルト値（全てdenied）
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

/**
 * GA4トラッキングを生成する。
 *
 * - 前提条件（dataLayer/gtagスタブ・Consent Modeデフォルト）は内部で setupGA4 を
 *   呼んで自ら満たす。呼び出し側は初期化手順を意識しなくてよい。
 *   Consent Modeの仕様上、gtag.jsロード *前* の呼び出しが必要なため、
 *   この関数もgtag.jsロード前（= Scriptコンポーネントのrender前）に呼ぶこと。
 * - 各メソッドは内部で ga4ReadyState.promise の完了を await してから gtag を呼ぶ
 *   （gtag.jsロード完了は同モジュールの createScriptHandlers が返す onLoad/onError で resolve する）
 *   → 呼び出し側は初期化順序を意識せず利用できる
 * - gtag呼び出しの例外はcatchしてログに記録（fire-and-forgetを維持）
 */
export function createGA4Tracking() {
  setupGA4();
  // ga4ReadyState.promiseの完了を待ってから実体のwindow.gtagを呼ぶラッパー。
  // ready前の呼び出しもキューイングされ、script読み込み後に順番に実行される。
  const sendGtag = async (...args: unknown[]): Promise<void> => {
    try {
      await ga4ReadyState.promise;
      window.gtag(...args);
    } catch (error) {
      logger.error({ error }, "GA4への送信に失敗");
    }
  };

  return {
    // gtag("set")を使い、config再呼び出しによるイベント重複を回避
    setUserId: (userId: string) => sendGtag("set", { user_id: userId }),
    // gtag公式仕様: user_idをnullに設定するとクリアされる
    clearUserId: () => sendGtag("set", { user_id: null }),
    // GA4推奨: page_location（絶対URL）と page_title を送信する
    // page_path は非推奨なため使用しない
    trackPageView: (path: string) =>
      sendGtag("event", "page_view", {
        page_location: `${window.location.origin}${path}`,
        page_title: document.title,
      }),
    trackEvent: (eventName: string, params?: Record<string, unknown>) =>
      sendGtag("event", eventName, params),
    // analytics_storage のみ更新する（mykenpoは広告配信系を使わないため、
    // ad_storage / ad_user_data / ad_personalization は setupGA4 のdefault("denied")のまま運用する）。
    updateConsent: (state: ConsentState) =>
      sendGtag("consent", "update", { analytics_storage: state }),
  };
}

/** GA4実装の戻り値型。GA4固有の副作用コンポーネント（GA4Tracker等）から参照される。 */
export type GA4Tracking = ReturnType<typeof createGA4Tracking>;

/**
 * GA4 への送信を止める・再開する。
 *
 * gtag.js 公式のオプトアウト（`window["ga-disable-<測定ID>"] = true`）を使う。
 * 立っている間は、手動の page_view・イベントに加え、GA4 の管理画面で有効にした
 * 拡張計測（履歴の変化によるページビュー・スクロール等）の送信も止まる。
 * 立てる前に起きた送信は止められないので、画面内の移動では呼び出し側のタイミングに注意する。
 */
export function setGA4Disabled(measurementId: string, disabled: boolean) {
  window[`ga-disable-${measurementId}`] = disabled;
}

/**
 * gtag.js を読み込む <Script> に渡す onLoad/onError ハンドラを生成する。
 *
 * ga4ReadyState 本体はモジュール内に閉じ、外部は発火点（onLoad/onError）だけを受け取る。
 * これにより、GA4Script のような React 薄ラッパを別ファイルに置いても
 * readyState の露出を避けつつ純ロジックと React を分離できる。
 *
 * - onLoad: send_page_view:false で config し、ready を解決 → 以後 page_view は手動送信
 * - onError: 広告ブロッカー等で読み込み失敗した場合も ready を解決する
 *   （gtag 自体は setupGA4 でスタブ化済みなので dataLayer に積まれるだけで実害なし）
 */
export function createScriptHandlers(measurementId: string) {
  return {
    onLoad: () => {
      window.gtag("config", measurementId, { send_page_view: false });
      ga4ReadyState.resolve();
    },
    onError: (event: Error | Event) => {
      logger.info(
        { error: event },
        "GA4スクリプトの読み込みに失敗（広告ブロッカーまたはネットワーク障害の可能性）",
      );
      ga4ReadyState.resolve();
    },
  };
}
